import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { Drawer } from '../ui/Drawer';
import { SelectField, TextField, Toggle, deletedRefProps } from './fields';
import ContactsPanel from './ContactsPanel';
import DocumentsPanel from './DocumentsPanel';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import { formatPhoneMaybe } from '../../utils/phone';
import { getGoogleMapsLoaderOptions } from '../../config/googleMaps';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import customersService, { CustomerFormOptions } from '../../services/customers.service';
import { keyAccountClass } from '../../pages/order-management/quoteColors';

/**
 * Bespoke edit/create drawer for Customers. Address is filled via a Google
 * Places lookup (like the Locations editor) rather than free entry; master
 * customer is a typeahead of MASTER customers; key account shows a colored
 * dropdown (MASTER) or colored inherited text (DIVISION); phone/fax auto-format;
 * name/address display Title-Cased but persist ALLCAPS (V1 upperit).
 */
const TABLE = 'customers';
const CUSTOMER_DOC_TYPE = 'SOP';

const REQUIRED: { key: string; label: string }[] = [
  { key: 'custname', label: 'Name' },
  { key: 'custadd', label: 'Address' },
  { key: 'custcity', label: 'City' },
  { key: 'custstate', label: 'State' },
  { key: 'custzip', label: 'Zip' },
  { key: 'salesrep', label: 'Sales Rep' },
  { key: 'marketcode', label: 'Market' },
];

const CUSTOMER_FLAGS = [
  { key: 'showcustomerco', label: 'Show Customer C/O' },
  { key: 'customlfd', label: 'Custom LFD' },
  { key: 'sealverification', label: 'Seal Verification' },
  { key: 'hidecustomerlabel', label: 'Hide Customer on Label' },
];
const BOL_FLAGS = [
  { key: 'custombl', label: 'Custom B/L' },
  { key: 'showrcvbol', label: 'Show RCV BOL' },
  { key: 'showlotnum', label: 'Show Lot #' },
  { key: 'showserialnum', label: 'Show Serial #' },
  { key: 'showrcvequipment', label: 'Show RCV Equipment #' },
  { key: 'showexpiration', label: 'Show Expiration Date' },
  { key: 'showrcvpo', label: 'Show RCV PO' },
  { key: 'showmanufacture', label: 'Show Manufacture Date' },
  { key: 'showrcvseal', label: 'Show RCV Seal' },
];
const FLAG_KEYS = [...CUSTOMER_FLAGS, ...BOL_FLAGS].map((f) => f.key);

const SCALAR_KEYS = [
  'custname', 'custadd', 'custadd2', 'custcity', 'custstate', 'custzip', 'custphone', 'custfax',
  'communication', 'keyaccount', 'salesrep', 'logisticscoordinator', 'custtype', 'marketcode',
  'masterrecno', 'salesforceid', 'custregion', 'allowbrokerage', 'brokeragecsr', 'complexityscore',
];
const ALL_KEYS = [...SCALAR_KEYS, ...FLAG_KEYS, 'additionalinfo', 'shippinginstructions'];

// Display Title-Cased, persisted ALLCAPS (V1 @upperitlist). State persists upper
// but isn't title-cased (2-letter code); zip is left untouched.
const TITLECASE_FIELDS = new Set(['custname', 'custadd', 'custadd2', 'custcity']);
const UPPER_ON_SAVE = new Set(['custname', 'custadd', 'custadd2', 'custcity', 'custstate']);

const NEW_DEFAULTS: TablemaintRow = {
  custtype: 'MASTER', allowbrokerage: 'Y', masterrecno: '0',
  showrcvbol: 'Y', showrcvequipment: 'Y', showrcvpo: 'Y', showrcvseal: 'Y',
  showlotnum: 'Y', showserialnum: 'Y', showexpiration: 'Y', showmanufacture: 'Y',
};

const s = (v: any): string => (v == null ? '' : String(v));
const dateDisplay = (v: any): string => {
  const sv = s(v);
  return !sv || sv.startsWith('0000') ? '' : sv;
};
const upperize = (k: string, v: any): string => (UPPER_ON_SAVE.has(k) ? s(v).toUpperCase() : s(v));

const LockedField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <label className="block text-sm font-medium text-muted mb-1">{label}</label>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
      {value === '' || value == null ? <span className="text-gray-400">—</span> : value}
    </div>
  </div>
);

const TextArea: React.FC<{ label: string; value: string; onChange: (v: string) => void; disabled?: boolean }> = ({
  label, value, onChange, disabled,
}) => (
  <div>
    <label className="block text-sm font-medium text-label mb-1">{label}</label>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} rows={3}
      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
  </div>
);

const CustomersEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? NEW_DEFAULTS);
  const [form, setForm] = useState<TablemaintRow>({});
  const [formDeleted, setFormDeleted] = useState(false);
  const [options, setOptions] = useState<CustomerFormOptions | null>(null);
  const [inUse, setInUse] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const { isLoaded: mapsLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);

  const wasDeleted = s(record.deleted) === 'Y';

  useEffect(() => {
    const ctrl = new AbortController();
    if (!isNew) {
      tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
      customersService.context(recno, ctrl.signal).then((c) => setInUse(c.inUse)).catch(() => {});
    }
    customersService.formOptions(ctrl.signal).then(setOptions).catch(() => {});
    return () => ctrl.abort();
  }, [isNew, recno]);

  // Seed the form from the record (Title-Case the display-cased fields).
  useEffect(() => {
    const f: TablemaintRow = {};
    ALL_KEYS.forEach((k) => { f[k] = TITLECASE_FIELDS.has(k) ? titleCase(s(record[k])) : s(record[k]); });
    if (isNew) Object.entries(NEW_DEFAULTS).forEach(([k, v]) => { if (!f[k]) f[k] = v as string; });
    setForm(f);
    setFormDeleted(s(record.deleted) === 'Y');
  }, [record, isNew]);

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  // Google Places appends its predictions dropdown (.pac-container) to
  // document.body, which renders BEHIND the drawer's top-layer <dialog>. Relocate
  // it into the dialog so the predictions are visible/clickable (mirrors Locations).
  useEffect(() => {
    const dialog = topRef.current?.closest('dialog');
    if (!dialog) return;
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement && node.classList.contains('pac-container')) {
            dialog.appendChild(node);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, [mapsLoaded]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));
  const isDivision = s(form.custtype) === 'DIVISION';

  // option adapters
  const opt = (arr: string[], blank = false) =>
    (blank ? [{ value: '', label: '— None —' }] : []).concat(
      arr.filter((v) => v !== '').map((v) => ({ value: v, label: titleCase(v) })),
    );
  const people = (arr: { username: string; displayname: string }[], blank = false) =>
    (blank ? [{ value: '', label: '— None —' }] : []).concat(
      arr.map((p) => ({ value: p.username, label: p.displayname })),
    );
  const marketOpts = (options?.markets ?? []).map((m) => ({ value: String(m.recno), label: m.market }));
  const keyAccountOpts = (options?.keyAccounts ?? []).map((k) => ({ value: k, label: titleCase(k) }));
  const custTypeOpts = (options?.custTypes ?? []).map((t) => ({ value: t, label: titleCase(t) }));
  const masterDesc = (m: { address: string; city: string; state: string }) =>
    [titleCase(m.address), titleCase(m.city), m.state].filter(Boolean).join(', ');
  const masterOpts = (options?.masterCustomers ?? []).map((m) => ({
    value: String(m.recno), label: titleCase(m.custname), labelSuffix: `#${m.recno}`,
    description: masterDesc(m),
  }));
  const masterKeyByRecno = useMemo(() => {
    const m = new Map<string, string>();
    (options?.masterCustomers ?? []).forEach((mc) => m.set(String(mc.recno), mc.keyaccount));
    return m;
  }, [options]);

  const errors = useMemo(() => {
    const errs: string[] = [];
    REQUIRED.forEach((r) => { if (!s(form[r.key]).trim()) errs.push(`${r.label} is required.`); });
    if (isDivision && !s(form.masterrecno).trim().replace(/^0$/, '')) {
      errs.push('Master Customer is required for Customer Type DIVISION.');
    }
    return errs;
  }, [form, isDivision]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];
  const invalid = (k: string) => showErrors && !s(form[k]).trim();

  const handleCustType = (v: string) => {
    setForm((p) => {
      const next: TablemaintRow = { ...p, custtype: v };
      if (v === 'MASTER') next.masterrecno = '0';
      return next;
    });
  };
  const handleMaster = (v: string) => {
    setForm((p) => ({ ...p, masterrecno: v, keyaccount: masterKeyByRecno.get(v) || p.keyaccount }));
  };
  const handleMarket = (v: string) => {
    const m = (options?.markets ?? []).find((mk) => String(mk.recno) === v);
    setForm((p) => ({ ...p, marketcode: v, ...(m && m.salesrep ? { salesrep: m.salesrep } : {}) }));
  };
  const onPlaceChanged = () => {
    const place = acRef.current?.getPlace();
    if (!place || !place.address_components) return;
    let streetNumber = '', route = '', city = '', state = '', zip = '';
    for (const c of place.address_components) {
      const t = c.types;
      if (t.includes('street_number')) streetNumber = c.long_name;
      else if (t.includes('route')) route = c.long_name;
      else if (t.includes('locality')) city = c.long_name;
      else if (t.includes('administrative_area_level_1')) state = c.short_name;
      else if (t.includes('postal_code')) zip = c.long_name;
    }
    const street = streetNumber ? `${streetNumber} ${route}` : route;
    setForm((p) => ({
      ...p,
      custadd: titleCase(street) || p.custadd,
      custcity: titleCase(city) || p.custcity,
      custstate: state || p.custstate,
      custzip: zip || p.custzip,
    }));
  };

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      if (isNew) {
        const payload: TablemaintRow = {};
        ALL_KEYS.forEach((k) => { payload[k] = upperize(k, form[k]); });
        await tablemaintService.create(TABLE, payload);
      } else {
        const patch: TablemaintRow = {};
        ALL_KEYS.forEach((k) => { if (upperize(k, form[k]) !== s(record[k])) patch[k] = upperize(k, form[k]); });
        if (formDeleted !== wasDeleted) patch.deleted = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length === 0) { onClose(); return; }
        await tablemaintService.update(TABLE, recno, patch);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, isNew, form, record, formDeleted, wasDeleted, recno, onClose, onSaved]);

  const sel = (key: string, label: string, opts: { value: string; label: string; labelSuffix?: string; description?: string }[], extra?: {
    required?: boolean; disabled?: boolean; clearable?: boolean; showDescriptions?: boolean; showSelectedInfo?: boolean; onChange?: (v: string) => void;
    /** [refName, deletedFlag] for an FK whose option list holds ACTIVE rows only —
     *  keeps a since-deleted target readable as "Name · #recno" plus the red note. */
    deletedRef?: [any, any];
  }) => (
    <SelectField label={label} required={extra?.required} disabled={isSaving || extra?.disabled}
      clearable={extra?.clearable} showDescriptions={extra?.showDescriptions} showSelectedInfo={extra?.showSelectedInfo} value={s(form[key])}
      {...(extra?.deletedRef
        ? deletedRefProps(label, extra.deletedRef[0], form[key], extra.deletedRef[1])
        : {})}
      onChange={extra?.onChange ?? ((v) => setField(key, v))} options={opts} />
  );
  const txt = (key: string, label: string, required = false, onBlur?: () => void) => (
    <TextField label={label} required={required} invalid={required && invalid(key)} disabled={isSaving}
      value={s(form[key])} onChange={(v) => setField(key, v)} onBlur={onBlur} />
  );
  const phone = (key: string, label: string) =>
    txt(key, label, false, () => setField(key, formatPhoneMaybe(s(form[key]))));

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Customer' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add Customer' : `Edit Customer — ${titleCase(s(record.custname))} (#${recno})`;
  const addrInputCls =
    'w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white';

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="60rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table="customers" recno={recno} label={titleCase(s(record.custname))} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Customer */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Customer</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {txt('custname', 'Name', true)}
            {/* Address lookup (Google Places) — fills the read-only address fields below. */}
            <div>
              <label className="block text-sm font-medium text-label mb-1">Address Lookup</label>
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                {mapsLoaded ? (
                  <Autocomplete onLoad={(ac) => { acRef.current = ac; }} onPlaceChanged={onPlaceChanged}
                    fields={['address_components']} types={['address']}>
                    <input type="text" placeholder="Search an address…" disabled={isSaving} className={addrInputCls} />
                  </Autocomplete>
                ) : (
                  <input type="text" placeholder="Loading maps…" disabled className={addrInputCls} />
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">{txt('custadd', 'Address', true)}</div>
            {txt('custadd2', 'Suite / Unit')}
            {txt('custcity', 'City', true)}
            {txt('custstate', 'State', true)}
            {txt('custzip', 'Zip', true)}
            {phone('custphone', 'Phone')}
            {phone('custfax', 'Fax')}
            {sel('communication', 'Pref. Communication', opt(options?.communication ?? [], true))}
          </div>
        </fieldset>

        {/* Classification */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Classification</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sel('custtype', 'Customer Type', custTypeOpts, { clearable: false, onChange: handleCustType })}

            {/* Key Account — colored dropdown (MASTER) / colored inherited text (DIVISION) */}
            {isDivision ? (
              <div>
                <label className="block text-sm font-medium text-label mb-1">Key Account</label>
                <div className="px-1 py-1">
                  {s(form.keyaccount)
                    ? <span className={`inline-block rounded px-2 py-1 text-sm font-medium ${keyAccountClass(form.keyaccount)}`}>{titleCase(form.keyaccount)}</span>
                    : <span className="text-gray-400">—</span>}
                </div>
                <p className="text-xs text-gray-400">Inherited from master customer.</p>
              </div>
            ) : (
              <div className={`rounded-lg p-2 ${s(form.keyaccount) ? keyAccountClass(form.keyaccount) : 'border border-gray-200 dark:border-gray-700'}`}>
                {sel('keyaccount', 'Key Account', keyAccountOpts, { clearable: false })}
              </div>
            )}

            {sel('marketcode', 'Market', marketOpts, { required: true, clearable: false, onChange: handleMarket })}
            {sel('salesrep', 'Sales Rep', people(options?.salesReps ?? []), { required: true })}
            {sel('logisticscoordinator', 'LC', people(options?.csrs ?? [], true))}
            {sel('brokeragecsr', 'Broker', people(options?.cstDispatchers ?? [], true))}
            {sel('custregion', 'Region', opt(options?.regions ?? [], false))}
            {txt('salesforceid', 'Salesforce ID')}
            {txt('complexityscore', 'Complexity Score')}
            {isDivision && (
              <div className="md:col-span-2">
                {sel('masterrecno', 'Master Customer', masterOpts, { required: true, showDescriptions: true, showSelectedInfo: true, onChange: handleMaster, deletedRef: [record.mastername, record.masterdeleted] })}
              </div>
            )}
          </div>
          <div className="mt-3">
            <Toggle label="Allow Brokerage" disabled={isSaving}
              checked={s(form.allowbrokerage) === 'Y'} onChange={(v) => setField('allowbrokerage', v ? 'Y' : 'N')} />
          </div>
        </fieldset>

        {/* Customer Options */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Customer Options</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            {CUSTOMER_FLAGS.map((f) => (
              <Toggle key={f.key} label={f.label} disabled={isSaving}
                checked={s(form[f.key]) === 'Y'} onChange={(v) => setField(f.key, v ? 'Y' : 'N')} />
            ))}
          </div>
        </fieldset>

        {/* Bill of Lading Options */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Bill of Lading Options</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8">
            {BOL_FLAGS.map((f) => (
              <Toggle key={f.key} label={f.label} disabled={isSaving}
                checked={s(form[f.key]) === 'Y'} onChange={(v) => setField(f.key, v ? 'Y' : 'N')} />
            ))}
          </div>
        </fieldset>

        {/* Notes */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Notes</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextArea label="Additional Info" disabled={isSaving}
              value={s(form.additionalinfo)} onChange={(v) => setField('additionalinfo', v)} />
            <TextArea label="Shipping Instructions" disabled={isSaving}
              value={s(form.shippinginstructions)} onChange={(v) => setField('shippinginstructions', v)} />
          </div>
        </fieldset>

        {!isNew && <ContactsPanel tablename="customers" recno={recno} />}
        {!isNew && <DocumentsPanel recno={recno} basePath="/customers-maint" docType={CUSTOMER_DOC_TYPE} />}

        {!isNew && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <LockedField label="Whse Cust #" value={s(record.whsecustnumber)} />
              <LockedField label="Created" value={dateDisplay(record.createdatetime)} />
              <LockedField label="Last Used" value={dateDisplay(record.lastuseddatetime)} />
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <Toggle label="Deleted" checked={formDeleted} disabled={isSaving || (inUse && !formDeleted)}
                onChange={setFormDeleted} />
              <p className="mt-1 ml-7 text-xs text-muted">
                {inUse && !wasDeleted
                  ? 'In use (product in inventory or pending change) — can’t be deleted right now.'
                  : 'Soft-deletes the customer — hidden from filters but preserved in history. Uncheck and save to restore.'}
              </p>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default CustomersEditDrawer;
